// src/App.jsx - Finding Your Voice Main Application
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { CharacterProvider } from './context/CharacterContext';
import { ProgressProvider } from './context/ProgressContext';
import { GamificationProvider } from './context/GamificationContext';
import { CourtyardProvider } from './context/CourtyardContext';

// Layout Components
import MainLayout from './components/shared/MainLayout';
import LoadingSpinner from './components/shared/LoadingSpinner';
import ScrollToTop from './components/shared/ScrollToTop';

// The way back to the courtyard
import FloatingCourtyardButton from './components/support/FloatingCourtyardButton';

// First-visit analytics consent notice (fleet standard wording)
import { AnalyticsConsentNotice } from '@ddd/analytics';
import { saveAnalyticsPreference } from './services/analytics';

// Lazy load pages
const HomePage = lazy(() => import('./pages/HomePage'));
const CourtyardPage = lazy(() => import('./pages/CourtyardPage'));
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const ModulesPage = lazy(() => import('./pages/ModulesPage'));
const ModuleDetailPage = lazy(() => import('./pages/ModuleDetailPage'));
const ToolkitPage = lazy(() => import('./pages/ToolkitPage'));
const ToolDetailPage = lazy(() => import('./pages/ToolDetailPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Auth pages
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const TokenExchange = lazy(() => import('./pages/auth/TokenExchange'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-courtyard-parchment">
    <LoadingSpinner message="The courtyard is preparing..." />
  </div>
);

// /modules/3 was an evening's address before they were called evenings.
const OldModuleAddress = () => {
  const { moduleId } = useParams();
  return <Navigate to={`/evenings/${moduleId}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <CharacterProvider>
        <ProgressProvider>
          <GamificationProvider>
            <CourtyardProvider>
              <ScrollToTop />
              <MainLayout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Auth Routes */}
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/auth/token-exchange" element={<TokenExchange />} />

                    {/* Home / Dashboard */}
                    <Route path="/dashboard" element={<HomePage />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    {/* Scene Setter */}
                    <Route path="/courtyard" element={<CourtyardPage />} />

                    {/* Assessment */}
                    <Route path="/assessment" element={<AssessmentPage />} />
                    <Route path="/results" element={<ResultsPage />} />

                    {/* The eight evenings. They were "modules" until October 2026;
                        the old addresses are sent on so a saved link still lands. */}
                    <Route path="/evenings" element={<ModulesPage />} />
                    <Route path="/evenings/:moduleId" element={<ModuleDetailPage />} />
                    <Route path="/modules" element={<Navigate to="/evenings" replace />} />
                    <Route path="/modules/:moduleId" element={<OldModuleAddress />} />

                    {/* Toolkit */}
                    <Route path="/toolkit" element={<ToolkitPage />} />
                    <Route path="/toolkit/:toolId" element={<ToolDetailPage />} />

                    {/* Resources */}
                    <Route path="/resources" element={<ResourcesPage />} />

                    {/* Profile & Progress */}
                    <Route path="/achievements" element={<AchievementsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />

                    {/* Fallback — a real 404 that keeps the URL */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </MainLayout>
              <FloatingCourtyardButton />
              {/* Shows once, to a visitor who has never been asked. onDecision
                  writes the choice into fyv_preferences, which boot reads; the
                  switch on the Profile page is the change-your-mind control. */}
              <AnalyticsConsentNotice
                appName="Finding Your Voice"
                privacyHref="/profile#your-data"
                accent="#C2703E"
                onDecision={saveAnalyticsPreference}
              />
            </CourtyardProvider>
          </GamificationProvider>
        </ProgressProvider>
      </CharacterProvider>
    </AuthProvider>
  );
}

export default App;
